/**
 * GoldFire Network (https://goldfire.me)
 * Copyright (c) 2012 - 2020, GoldFire Studios, Inc.
 * http://goldfirestudios.com
 */

import {shortUsernames} from '../utils';

/**
 * Set the sprite ID of the sound to play.
 * @param  {Object} state Full state reference.
 * @param  {String} sound Sound to play.
 */
export const playSound = (state, sound) => {
  state.toolbar.sound = sound;
};

/**
 * Toggles tabs open/closed on the toolbar.
 * If clicking the same tab, close the panel.
 * @param  {Object} state Full state reference.
 * @param  {String} tab   The tab to make active.
 */
export const toggleTab = (state, tab) => {
  // Reset the history for views.
  const currentTab = state[state.toolbar.activeTab];
  if (currentTab) {
    currentTab.activeView = currentTab.defaultView;
  }
  state.panel.history.splice(0);

  // Switch to the new tab or close the panel.
  const sameTab = (state.toolbar.activeTab === tab);
  state.toolbar.transition = (state.toolbar.activeTab === 'empty' || sameTab) ? 'slide' : '';
  state.toolbar.activeTab = sameTab ? 'empty' : tab;

  // Reset the panel data when the tab is empty.
  if (state.toolbar.activeTab === 'empty') {
    state.panel.buttons = [];
    state.panel.status = null;
    state.panel.altStatus = null;
    state.panel.altIcon = null;
    state.panel.wide = false;
    state.panel.footerView = null;
    state.panel.buttonTransition = null;
  }
};

/**
 * Updates the editor title value (only used for forum threads).
 * @param  {Object} state Full state reference.
 * @param  {String} title Title to set state editor title to.
 */
export const updateTitle = (state, title) => {
  state.editor.title = title;
};

/**
 * Toggle whether or not to show the panel loading indicator.
 * @param  {Object} state Full state reference.
 * @param  {Boolean} value (optional) Value to set loading to.
 */
export const togglePanelLoading = (state, value) => {
  state.panel.loading = typeof value === 'boolean' ? value : !state.panel.loading;
};

/**
 * Set panel data when mounting a different tab.
 * This will update the header, footer, etc.
 * @param {Object} state Full state reference.
 * @param {Object} data  Panel data to be set.
 */
export const setPanelData = (state, data) => {
  const defaultInput = {placeholder: '', value: '', show: false};

  state.panel.title = data.title;
  state.panel.buttons = data.buttons;
  state.panel.input = data.input ? Object.assign({}, data.input, {show: true}) : defaultInput;
  state.panel.status = data.status || null;
  state.panel.altStatus = data.altStatus || null;
  state.panel.altIcon = data.altIcon || null;
  state.panel.altMethod = data.altMethod || {};
  state.panel.wide = data.wide || false;
  state.panel.headerView = data.headerView || null;
  state.panel.footerView = data.footerView || null;
  state.panel.buttonTransition = data.buttonTransition || null;
};

/**
 * Set panel width without affecting other panel data.
 * @param  {Object} state Full state reference.
 * @param  {Boolean} wide  Set to wide or not.
 */
export const setPanelWide = (state, wide) => {
  state.panel.wide = wide;
};

/**
 * Set panel status without affecting other panel data.
 * @param {Object} state Full state reference.
 * @param {Object} status  New panel status to be set.
 */
export const setPanelAltStatus = (state, status) => {
  state.panel.altStatus = status || null;
};

/**
 * Set panel icon and method without affecting other panel data.
 * @param {Object} state Full state reference.
 * @param {Object} status  New panel icon to be set.
 * @param {Object} status  New panel method to be set.
 */
export const setPanelAltIcon = (state, {altIcon, altMethod}) => {
  state.panel.altIcon = altIcon || null;
  state.panel.altMethod = altMethod || null;
};

/**
 * Sets the history to a specific value.
 * This is generally done automatically, but can be useful
 * for UX purposes in certain cases.
 * @param  {Object} state   Full state reference.
 * @param  {Array} history Array of back-button history views.
 */
export const setHistory = (state, history) => {
  state.panel.history = history;
};

/**
 * Push history onto the stack.
 * @param  {Object} state   Full state reference.
 * @param  {String} history View or object for complex actions. Defaults to current view.
 */
export const addHistory = (state, history) => {
  if (!history) {
    const activeTab = state[state.toolbar.activeTab];
    if (!activeTab) {
      return;
    }
    history = activeTab.activeView;
  }

  state.panel.history.push(history);
};

/**
 * Remove latest history item.
 * @param  {Object} state   Full state reference.
 */
export const popHistory = (state) => {
  state.panel.history.pop();
};

/**
 * Set the active view of the active tab.
 * @param  {Object} state   Full state reference.
 * @param  {String} newView The new view to switch to.
 */
export const setView = (state, newView) => {
  const activeTab = state[state.toolbar.activeTab];

  if (!activeTab) {
    return;
  }

  activeTab.activeView = newView;
};

/**
 * Set view and update history with current view.
 * @param  {Object} state   Full state reference.
 * @param  {String} newView The new view to switch to.
 */
export const changeView = (state, newView) => {
  addHistory(state); // Add the previous view to the history.
  setView(state, newView);
};

/**
 * Toggle a tab open/close and switch to a sub-view.
 * @param  {Object} state                     Full state reference.
 * @param  {String} options.tab               Tab to toggle.
 * @param  {String} options.view              View to go to.
 */
export const toggleTabView = (state, {tab, view}) => {
  const curTab = state.toolbar.activeTab;

  if (tab !== curTab || (tab === curTab && view === state[tab].activeView)) {
    toggleTab(state, tab);
  }

  addHistory(state);

  setView(state, view);
};

/**
 * Set the tab and active view together so that the default tab view doesn't load first.
 * @param  {Object} state        Full state reference.
 * @param  {String} options.tab  Tab to load.
 * @param  {String} options.view View to switch to.
 */
export const setTabView = (state, {tab, view}) => {
  const currentTab = state[state.toolbar.activeTab];
  if (currentTab) {
    currentTab.activeView = currentTab.defaultView;
  }

  state.toolbar.transition = (state.toolbar.activeTab === 'empty') ? 'slide' : '';

  state[tab].activeView = view;
  state.toolbar.activeTab = tab;
};

/**
 * Toggle a social feed update like.
 * @param  {Object} state             Full state reference.
 * @param  {String} options.updateId  Update on which to toggle like.
 */
export const toggleLike = (state, {updateId, like}) => {
  const uid = like;
  const update = state.socialFeed.updates.find(u => u._id === updateId);
  const likeIndex = update.likes.findIndex(l => l === uid);

  if (likeIndex < 0) {
    update.likes.push(uid);
  } else {
    update.likes.splice(likeIndex, 1);
  }
};

/**
 * Update the value of the input in the panel.
 * @param  {Object} state    Full state reference.
 * @param  {String} newValue Value of the input.
 */
export const updateInput = (state, newValue) => {
  state.panel.input.value = newValue;
};

/**
 * Selects a tab in the Profile view.
 * @param  {Object} state    Full state reference.
 * @param  {String} tabName  The tab (component name) to make active.
 */
export const selectProfileTab = (state, tabName) => {
  state.launcher.profile.activeTab = tabName;
};

/**
 * Selects a tab in the Profile view.
 * @param  {Object} state    Full state reference.
 * @param  {String} tabName  The tab (component name) to make active.
 */
export const setProfileData = (state, data) => {
  state.launcher.profile = data;
};

/**
 * Set the forum ban status of this player ont he client.
 * @param  {Object} state Full state reference.
 * @param  {Number} ban   1 or 0.
 */
export const setProfileForumBan = (state, ban) => {
  state.launcher.profile = Object.assign({}, state.launcher.profile, {forumban: ban});
};

/**
 * Add a friend to a profile's friend list.
 * @param  {Object} state Full state reference.
 * @param  {Object} friend
 */
export const addProfileFriend = (state, friend) => {
  state.launcher.profile.friends.push(friend);
};

/**
 * Remove a friend from a profile's friend list.
 * @param  {Object} state Full state reference.
 * @param  {Object} friend
 */
export const removeProfileFriend = (state, uid) => {
  const index = state.launcher.profile.friends.findIndex(f => f.uid === uid);

  if (index > -1) {
    state.launcher.profile.friends.splice(index, 1);
  }
};

/**
 * Remove a social feed update.
 * @param  {Object} state Full state reference.
 * @param  {String} id    Update post ID.
 */
export const removeSocialFeedUpdate = (state, id) => {
  const index = state.socialFeed.updates.findIndex(u => u._id === id);

  if (index > -1) {
    state.socialFeed.updates.splice(index, 1);
  }
};

/**
 * Remove a social feed update.
 * @param  {Object}  state Full state reference.
 * @param  {String}  id    Update post ID.
  * @param {String}  uid   Commenter UID.
 * @param  {String}  t     Time comment was posted.
 */
export const removeSocialFeedComment = (state, {id, uid, t}) => {
  const update = state.socialFeed.updates.find(u => u._id === id);
  const index = update.comments.findIndex(c => c.player.uid === uid && c.t === t);

  if (index > -1) {
    update.comments.splice(index, 1);
  }
};

/**
 * Add a medal to a profile's medal list.
 * @param  {Object} state Full state reference.
 * @param  {Object} medal
 */
export const addProfileMedal = (state, medal) => {
  const game = state.launcher.profile.games.find(g => g.game === medal.gid);
  game.earnedMedals.push(medal);
};

/**
 * Add a game to a profile's game list.
 * @param  {Object} state               Full state reference.
 * @param  {Object} options.gid         Game ID.
 * @param  {Object} options.name        Game name.
 * @param  {Object} options.medalCount  How many medals are available in the game.
 */
export const addProfileGame = (state, {gid, medalCount, name}) => {
  state.launcher.profile.games.push({
    active: true,
    earnedMedals: [],
    game: gid,
    name,
    medalCount,
    rank: 'N/A',
    t: Date.now(),
  });
};

/**
 * Update the leaderboard medal data (whether adding or replacing).
 * @param  {Object}  state           Full state reference.
 * @param  {Array}   options.medals  List of medals.
 * @param  {Boolean} options.update  True to update the existing list. (Replaces it by default.)
 */
export const setLeaderboardMedals = (state, {medals = [], update}) => {
  if (update) {
    // Find matching medals in current list and replace their data.
    state.leaderboards.medals = state.leaderboards.medals.map(curMedal => {
      const medal = medals.find(medal => medal.gid === curMedal.gid && medal.img === curMedal.img);
      if (medal) {
        return Object.assign({}, curMedal, medal);
      }
      return curMedal;
    });
  } else {
    state.leaderboards.medals = medals;
  }
};

/**
 * Update user's medal progress.
 * @param  {Object} state Full state reference.
 * @param  {Object} medal
 */
export const updateMedalProgress = (state, medal) => {
  setLeaderboardMedals(state, {medals: [medal], update: true});
};

/**
 * Update any user data that has changed.
 * @param {Object} state Full state reference.
 * @param {Object} data  Data to update.
 */
export const setUserData = (state, data) => {
  const params = [
    'auth',
    'user',
    'id',
    'avatar',
    'games',
    'role',
    'notices',
    'forumban',
    'emailcheck',
    'email',
    'guest',
    'verify',
    'isGdpr',
    'gdpr',
    'ltv',
  ];
  params.forEach((param) => {
    if (typeof data[param] !== 'undefined') {
      state.user[param] = data[param];
    }
  });
};

/**
 * Update the auth data (for login or signup).
 * @param {Object} state Full state reference.
 * @param {Object} data  Data to update.
 */
export const setAuthData = (state, data) => {
  const params = ['email', 'pass', 'emailTaken', 'activeView', 'showVerify', 'showTerms'];
  params.forEach((param) => {
    if (typeof data[param] !== 'undefined') {
      state.auth[param] = data[param];
    }
  });
};

/**
 * Handle a successful login and set the cookies, data, etc.
 * @param  {Object} state         Full state reference.
 * @param  {String} options.email E-mail logged-in with.
 * @param  {Object} options.data  Login data (cookie).
 */
export const processLogin = (state, {email = '', data}) => {
  // Set the session cookie.
  const expires1 = new Date(Date.now() + 31536000000).toGMTString();
  const expires2 = new Date(Date.now() + 31536000000).toGMTString();
  document.cookie = `sid=${data.cookie}; expires=${expires1}; path=/`;
  document.cookie = `lastLogin=${email}; expires=${expires2}; path=/`;
  if (data.guestKey) {
    document.cookie = `guestKey=${data.guestKey}; expires=${expires1}; path=/`;
  }
  if (data.secret) {
    document.cookie = `auth=${data.secret}; expires=${expires1}; path=/`;
  }

  // Update the user state.
  state.user.auth = true;
  state.user.user = data.user;
  state.user.uid = data.uid;
  state.user.avatar = data.avatar;
  state.user.games = data.games;
  state.user.role = data.role;
};

/**
 * Update the list of players displayed on the directory page.
 * @param {Object} state   Full state reference.
 * @param {Array} players List of players.
 */
export const setDirectoryPlayers = (state, players) => {
  state.directory.players = players || [];
};

/**
 * Set the visible items in the passed collection.
 * @param  {Array}   list            Collection to update.
 * @param  {Object}  options
 * @param  {Array}   options.items   New items.
 * @param  {Boolean} options.concat  True to add to the list, otherwise overwrite it.
 * @param  {Boolean} options.prepend True to add to the beginning list, otherwise to the end.
 * @returns {Array}
 */
const setItems = (list, {items = [], concat, prepend}) => {
  if (concat && !prepend) {
    return [...list, ...items];
  } else if (prepend) {
    return [...items, ...list];
  }

  return items;
};

/**
 * Update the chat messages data (whether adding or replacing).
 * @param  {Object}  state           Full state reference.
 * @param  {Array}   options.items   List of chat threads.
 * @param  {Boolean} options.concat  True to add to the list, otherwise overwrite it.
 * @param  {Boolean} options.prepend True to add to the beginning list, otherwise to the end.
 */
export const setChatThreadList = (state, {items = [], concat, prepend}) => {
  state.chat.messages = setItems(state.chat.messages, {items, concat, prepend})
    .sort((a, b) => {
      if (new Date(a.t) > new Date(b.t)) {
        return -1;
      }
      if (new Date(a.t) < new Date(b.t)) {
        return 1;
      }
      return 0;
    });
};

/**
 * Toggle forum search bar visibility.
 * @param  {Object}  state  Full state reference.
 */
export const toggleForumSearch = (state) => {
  state.forums.searchIsVisible = !state.forums.searchIsVisible;
};

/**
 * Toggle forum search bar visibility.
 * @param  {Object}  state          Full state reference.
 * @param  {String}  options.query  New query.
 */
export const setForumSearchQuery = (state, {query}) => {
  state.forums.searchQuery = query;
};

/**
 * Update list of forum categories with unread thread counts.
 * @param  {Object} state                Full state reference.
 * @param  {Array} options.categoryList  List of categories.
 */
export const setForumCategoryList = (state, {categoryList}) => {
  categoryList.forEach((category) => {
    const categoryState = state.forums.categories.find(cat => cat.gid === category.gid);
    if (categoryState) {
      categoryState.unread = category.unread;
    }
  });
};

/**
 * Increment forum notifications.
 * @param  {Object} state  Full state reference.
 * @param  {Number} number Number to increment by.
 */
export const incForumNotifications = (state, number) => {
  if (number !== 0) {
    state.forums.notifications += number;
    if (state.forums.notifications < 0) {
      state.forums.notifications = 0;
    }
  } else {
    state.forums.notifications = 0;
  }
};

/**
 * Mark a post as unread
 * @param  {Object} state        Full state reference.
 * @param  {Array} options.post  Post to mutate.
 */
export const markPostUnread = (state, {index}) => {
  state.forums.activeThread.posts[index].unread = true;
};

/**
 * Either reset player status cache or set the values of many.
 * @param  {Object} state   Full state reference.
 * @param  {Array}  players Players to set in the cache.
 */
export const setPlayerStatus = (state, players = []) => {
  // Reset the list if the array is empty.
  if (players.length === 0) {
    state.status.players = {};
    return;
  }

  // Convert the array of data.
  const status = {};
  players.forEach((player) => {
    status[player.uid] = {
      status: player.status,
      t: player.t,
    };

    if (player.away) {
      status[player.uid].away = true;
    }
  });

  // Replace the players status state.
  state.status.players = Object.assign({}, state.status.players, status);
};

/**
 * Add a player's values to the player status cache.
 * @param  {Object} state  Full state reference.
 * @param  {Object} player Player data to add to cache.
 */
export const addPlayerStatus = (state, player) => {
  const prev = state.status.players[player.uid] || {};
  const newStatus = {
    [player.uid]: {
      status: player.status || prev.status,
      t: player.t || prev.t,
    },
  };

  if (player.away) {
    newStatus[player.uid].away = true;
  }
  if (typeof player.game !== 'undefined') {
    newStatus[player.uid].game = player.game;
  }

  state.status.players = Object.assign({}, state.status.players, newStatus);
};

/**
 * Set data about the chat message thread you are switching to.
 * @param  {Object} state        Full state reference.
 * @param  {String} options.id   Chat thread ID.
 * @param  {String} options.user Username of other member.
 */
export const viewChatMessage = (state, {id, user, uid, name}) => {
  if (id) {
    state.chat.activeMessage.id = id;
  }
  if (user) {
    state.chat.activeMessage.user = user;
  }
  if (name) {
    state.chat.activeMessage.name = name;
  }
  if (uid) {
    state.chat.activeMessage.uid = uid;
  }
};

/**
 * Set the chat thread data (messages and players).
 * @param  {Object} state            Full state reference.
 * @param  {Array}  options.items Messages to add to the list.
 * @param  {Array} options.players  Players in the thread.
 * @param  {Boolean} options.ignore If this user can message the other.
 * @param  {Boolean} options.concat  True to add to the list, otherwise overwrite it.
 * @param  {Boolean} options.prepend True to add to the beginning list, otherwise to the end.
 */
export const setChatThread = (state, {items = [], players, ignore = false, concat, prepend, name = null}) => {
  state.chat.activeMessage.ignore = ignore;
  state.chat.activeMessage.name = name;

  if (items && items.length > 0) {
    state.chat.activeMessage.messages = setItems(state.chat.activeMessage.messages, {items, concat, prepend});
  }

  // Add player data.
  if (players) {
    state.chat.activeMessage.players = players;

    // Make sure the username is showing correctly.
    const plrs = players.filter(plr => plr.uid !== state.user.uid);
    const user = shortUsernames(plrs);
    if (plrs.length > 0) {
      viewChatMessage(state, {user, uid: plrs[0].uid});
      setPlayerStatus(state, plrs);
    }
  }
};

/**
 * Remove the chat typing message from a specific player.
 * @param  {Object} state  Full state reference.
 * @param  {Number} player Index of the player to remove from.
 */
export const remChatTyping = (state, {player, scroll}) => {
  const index = state.chat.activeMessage.messages.findIndex(chat => chat.typing && chat.player === player);
  if (index >= 0) {
    state.chat.activeMessage.messages.splice(index, 1);
    if (scroll) {
      state.panel.scrollTop -= 50;
    }
  }
};

/**
 * TODO: This should check that the active message ID matches this message.
 *
 * Add new chat message to the end of the active chat thread.
 * @param  {Object} state Full state reference.
 * @param  {Object} data  Chat message data.
 */
export const addChatMessage = (state, data) => {
  if (state.chat.activeMessage.id !== data.cid) {
    return;
  }

  state.chat.activeMessage.messages.push(data);

  // Play a sound based on who sent the message.
  if (!data.typing) {
    playSound(state, data.player ? 'chatRec' : 'chatSend');
  }
};

/**
 * Reset the state of the active chat message.
 * @param  {Object} state Full state reference.
 */
export const resetChatThread = (state) => {
  state.chat.activeMessage.messages = [];
  state.chat.activeMessage.players = [];
};

/**
 * Set the list of players in a group chat settings view.
 * @param  {Object} state   Full state reference.
 * @param  {Array} players Players in the group.
 */
export const setChatSettingsPlayers = (state, players) => {
  state.chat.settings.players = players;
};

/**
 * Set the players and switch the view to the chat settings.
 * @param  {Object} state           Full state reference.
 * @param  {Array} options.players Players to show.
 */
export const viewChatSettings = (state, {players}) => {
  setChatSettingsPlayers(state, players);
  toggleTabView(state, {tab: 'chat', view: 'chat-settings-view'});
};

/**
 * Set the type of friend list we are showing to determine the action taken.
 * @param  {Object} state           Full state reference.
 * @param  {String} options.type    'friend', 'chat' or 'group'
 * @param  {Array}  options.exclude Players to exclude from the list. (If existing group members.)
 */
export const setFriendListType = (state, {type, exclude}) => {
  state.chat.friendListType = type;
  state.chat.friendListExclude = exclude || [];
  toggleTabView(state, {tab: 'chat', view: 'friends-view'});
};

/**
 * Update the online friends list.
 * @param  {Object} state   Full state reference.
 * @param  {Array}  friends List of friends.
 */
export const setOnlineFriends = (state, friends = []) => {
  state.index.friends = friends;
};

/**
 * Update the player data for the friends list.
 * @param  {Object} state   Full state reference.
 * @param  {Array}  friends List of friends.
 */
export const setFriends = (state, friends = []) => {
  state.friends.players = friends;
};

/**
 * Set and order the chat friends list.
 * @param  {Object} state           Full state reference.
 * @param  {Array}  options.items   List of friends.
 * @param  {Boolean} options.concat  Whether or not to concat to current data.
 * @param  {Boolean} options.prepend Whether or not to prepend the data.
 */
export const setFriendsList = (state, {items = [], concat, prepend}) => {
  state.friends.players = setItems(state.friends.players, {items, concat, prepend})
    .sort((a, b) => {
      if (a.t > b.t) {
        return -1;
      }
      if (a.t < b.t) {
        return 1;
      }
      return 0;
    });
};

/**
 * Store the full friend data in a cache.
 * @param  {Object} state Full state reference.
 * @param  {Array} cache Player cache data.
 */
export const setFriendsCache = (state, cache) => {
  state.friends.cache = cache;
};

/**
 * Update the friends cache with new state.
 * @param  {Object} state Full state reference.
 * @param  {Object} data  Data to update.
 */
export const updateFriendsCache = (state, data) => {
  const user = state.friends.cache[data.uid];
  if (user && data.t) {
    user.t = data.t;
    user.status = data.status;
  }
  if (user && data.game) {
    user.game = data.game;
  }
};

/**
 * Set the global infinite scrolling state data.
 * @param  {Object} state Full state reference.
 * @param  {Object} data  Infinite scrolling state to set.
 */
export const setScrollData = (state, data) => {
  const params = ['start', 'end', 'direction', 'loading'];
  params.forEach((param) => {
    if (typeof data[param] !== 'undefined') {
      state.scroll[param] = data[param];
    }
  });
};

/**
 * Update the status of the scroll area.
 * @param  {Object} state Full state reference.
 * @param  {Boolean} start   Whether you've scrolled to the start or not.
 * @param  {Boolean} end   Whether you've scrolled to the end or not.
 * @param  {Number} top   The scrollTop position.
 */
export const setScrollAreaPosition = (state, {start, end, top}) => {
  if (top === 0) {
    state.panel.scrollStart = true;
  }
  if (typeof start !== 'undefined') {
    state.panel.scrollStart = start;
  }
  if (typeof end !== 'undefined') {
    state.panel.scrollEnd = end;
  }
  if (typeof top === 'number') {
    state.panel.scrollTop = top;
  }
};

/**
 * Set whether scroll area should scroll to the bottom.
 * @param  {Object}  state           Full state reference.
 * @param  {Boolean} bottom  Defaults to true.
 */
export const scrollToBottom = (state, bottom = true) => {
  state.panel.scrollToBottom = bottom;
};

/**
 * This toggle will cause the scrollbars plugin to update when set to true.
 * @param  {Object} state Full state reference.
 */
export const updateScrollBars = (state) => {
  state.panel.updateScroll = !state.panel.updateScroll;
};

/**
 * Scroll to top of scroll area.
 * @param  {Object}  state           Full state reference.
 */
export const scrollToTop = (state) => {
  setScrollAreaPosition(state, {top: 0});
  updateScrollBars(state);
};

/**
 * set the html value of the editor.
 * @param  {object} state full state reference.
 * @param  {string} html  html of editor input.
 */
export const setEditorValue = (state, html) => {
  state.editor.value = html;
};

/**
 * set the html value of the editor.
 * @param  {object} state full state reference.
 * @param  {string} html  html of editor input.
 */
export const setEditorInitialValue = (state, html) => {
  state.editor.initialValue = html;
};

/**
 * Track whether or not the enter key is down while in the text editor.
 * @param  {Object} state Full state reference.
 * @param  {Boolean} down
 */
export const setEditorEnterDown = (state, down) => {
  state.editor.enterDown = down;
};

/**
 * Reset the editor to any empty state (usually after a message is sent).
 * @param  {Object} state Full state reference.
 * @param  {Boolean} reset If the state is reset or not.
 */
export const setEditorReset = (state, reset) => {
  state.editor.reset = reset;
  state.editor.value = '';
  state.editor.title = '';
};

/**
 * Change the active leaderboard category that is being viewed.
 * @param  {Object} state          Full state reference.
 * @param  {String} newLeaderboard The leaderboard to make active.
 */
export const changeLeaderboard = (state, newLeaderboard) => {
  state.leaderboards.activeCategory = newLeaderboard;
};

/**
 * Switch view to a specific leaderboard.
 * @param  {Object} state    Full state reference.
 * @param  {String} category Leaderboard to switch to.
 */
export const viewLeaderboard = (state, category) => {
  changeLeaderboard(state, category);
  toggleTabView(state, {tab: 'leaderboards', view: 'leaderboard-view'});
};

/**
 * Set which keyboard view is active for editor (poll, tags, etc).
 * @param  {Object} state Full state reference.
 * @param  {String} view  'poll', 'tags'.
 */
export const setEditorView = (state, view) => {
  state.editor.view = view;
};

/**
 * Set the editor poll values for submission to the server.
 * @param  {Object} state Full state reference.
 * @param  {Object} data  Contains question and answers, or null to reset.
 */
export const setEditorPoll = (state, data) => {
  if (data) {
    state.editor.poll.q = data.q;
    state.editor.poll.a = data.a;
  } else {
    state.editor.poll = {};
  }
};

/**
 * Set editor title error state.
 * @param  {Object}  state    Full state reference.
 * @param  {Boolean} isError  Whether there is a title error.
 */
const setEditorTitleError = (state, isError = true) => {
  state.editor.titleError = isError;
};

/**
 * Step through the new forum thread displays.
 * @param  {Object} state Full state reference.
 */
export const newThreadNext = (state) => {
  const {title, value} = state.editor;
  const titleIsEmpty = title === '';
  const valueIsEmpty = (value === '' || value === '<div><br></div>');

  setEditorTitleError(state, titleIsEmpty);

  if (titleIsEmpty || valueIsEmpty) {
    return;
  }

  // Show the correct view and update buttons.
  if (state.editor.hasPoll && !state.editor.poll.q) {
    setEditorView(state, 'poll');
  } else if (state.editor.selectedTags.length === 0) {
    setEditorView(state, 'tags');

    state.panel.buttons = [
      {label: 'Cancel', view: {mutation: 'toggleEditor', data: {type: 'new'}}},
      {label: 'Publish', view: {action: 'createForumThread'}},
    ];
  }
};

/**
 * Toggle the editor poll option.
 * @param  {Object} state Full state reference.
 * param  {Boolean} show  Show or not to show, that is the question.
 */
export const toggleEditorPoll = (state, show) => {
  state.editor.hasPoll = typeof show === 'undefined' ? !state.editor.hasPoll : show;
};

/**
 * Toggle the editor emoji display opened/closed.
 * @param  {Object} state Full state reference.
 * @param  {Boolean} show  Show or not to show, that is the question.
 */
export const toggleEditorEmoji = (state, show) => {
  state.editor.emoji = typeof show === 'undefined' ? !state.editor.emoji : show;
};

/**
 * Toggle the editor media display opened/closed.
 * @param  {Object} state Full state reference.
 * @param  {Boolean} show  Show or not to show, that is the question.
 */
export const toggleEditorMedia = (state, show) => {
  state.editor.media = typeof show === 'undefined' ? !state.editor.media : show;
};

/**
 * Toggle the filter tags display in the forums.
 * @param  {Object} state Full state reference.
 */
export const toggleShowTags = (state, {show, filter, select}) => {
  state.forums.tags.show = typeof show === 'undefined' ? !state.forums.tags.show : show;
  state.forums.tags.filter = filter || false;
  state.forums.tags.select = select || false;

  // Update the buttons.
  if (state.forums.tags.show) {
    if (state.forums.tags.filter) {
      state.panel.buttons = [
        {label: 'Close', view: {mutation: 'toggleShowTags', data: {filter: false, select: false}}},
      ];
    }
  } else if (!state.forums.tags.select) {
    state.panel.buttons = [
      {label: 'New Thread', view: {mutation: 'toggleEditor', data: {type: 'new'}}},
      {label: 'Filter Tags', view: {mutation: 'toggleShowTags', data: {filter: true, select: false}}},
    ];
  }
};

/**
 * Toggle the value on/off of a tag to filter by.
 * @param  {Object} state Full state reference.
 * @param  {Object} data  Tag data.
 */
export const toggleForumFilterTag = (state, data) => {
  const index = state.forums.filteredTags.indexOf(data);

  if (index === -1) {
    state.forums.filteredTags.push(data);
  } else {
    state.forums.filteredTags.splice(index, 1);
  }
};

/**
 * Toggle the value on/off of a tag to filter by.
 * @param  {Object} state Full state reference.
 * @param  {Object} data  Tag data.
 */
export const toggleForumSelectTag = (state, data) => {
  const index = state.editor.selectedTags.indexOf(data);

  if (index === -1) {
    // Only allow 2 tags to be added to a thread.
    if (state.editor.selectedTags.length >= 2) {
      return;
    }

    state.editor.selectedTags.push(data);
  } else {
    state.editor.selectedTags.splice(index, 1);
  }
};

/**
 * Set the selected tags for the new thread.
 * @param  {Object} state Full state reference.
 * @param  {Array} tags  Selected tags.
 */
export const setSelectedEditorTags = (state, tags) => {
  state.editor.selectedTags = tags;
};

/**
 * Set the filtered tags for the forums.
 * @param  {Object} state Full state reference.
 * @param  {Array} tags  Filtered tags.
 */
export const setForumFilterTags = (state, tags) => {
  state.forums.filteredTags = tags;
};

/**
 * Toggles the various different text editors opened/closed.
 * @param  {Object} state        Full state reference.
 * @param  {String} options.type Which type of editor.
 * @param  {String} options.pid  Unique post identifier.
 */
export const toggleEditor = (state, {type, pid}) => {
  state.editor.show = !state.editor.show;
  state.editor.type = type;

  // Update the buttons and any other custom data.
  if (type === 'new') {
    if (state.editor.show) {
      state.panel.buttons = [
        {label: 'Cancel', view: {mutation: 'toggleEditor', data: {type: 'new'}}},
        {label: 'Continue', view: {mutation: 'newThreadNext'}},
      ];
    } else {
      state.panel.buttons = [
        {label: 'New Thread', view: {mutation: 'toggleEditor', data: {type: 'new'}}},
        {label: 'Filter Tags', view: {mutation: 'toggleShowTags', data: {filter: true, select: false}}},
      ];

      setEditorPoll(state);
      setEditorView(state, 'editor');
      toggleEditorPoll(state, false);
      toggleShowTags(state, {show: false});
    }
  } else if (type === 'reply') {
    if (state.editor.show) {
      state.editor.prevButtons = state.panel.buttons;
      state.panel.buttons = [
        {label: 'Cancel', view: {mutation: 'toggleEditor', data: {type: 'reply'}}},
        {label: 'Publish', view: {action: 'postForumReply'}},
      ];
    } else {
      state.panel.buttons = state.editor.prevButtons;
      state.panel.prevButtons = [];
    }
  } else if (type === 'edit') {
    if (state.editor.show) {
      state.editor.prevButtons = state.panel.buttons;
      state.panel.buttons = [
        {label: 'Cancel', view: {mutation: 'toggleEditor', data: {type: 'edit'}}},
        {label: 'Publish', view: {action: 'editForumPost', data: pid}},
      ];
    } else {
      state.panel.buttons = state.editor.prevButtons;
      state.panel.prevButtons = [];
    }
  } else if (type === 'chat') {
    state.panel.buttonTransition = 'fade';
    if (state.editor.show) {
      state.panel.buttons = [
        {label: 'Cancel', view: {mutation: 'toggleEditor', data: {type: 'chat'}}},
        {label: 'Send', view: {action: 'sendChatMessage'}},
      ];
    } else {
      state.panel.buttons = [];
    }
  } else if (type === 'feed') {
    state.panel.buttonTransition = 'fade';
    if (state.editor.show) {
      state.panel.buttons = [
        {label: 'Cancel', view: {mutation: 'toggleEditor', data: {type: 'feed'}}},
        {label: 'Send', view: {action: 'postSocialUpdate'}},
      ];
    } else {
      state.panel.buttons = [
        {label: 'New Post', view: {mutation: 'toggleEditor', data: {type: 'feed'}}},
      ];
    }
  }

  if (state.editor.show === false) {
    state.editor.initialValue = '';
  }
};

/**
 * Set the game ID of the forum category view.
 * @param  {Object} state Full state reference.
 * @param  {Number} gid    Game ID.
 */
export const setForumCategory = (state, gid) => {
  state.forums.currentCategory = gid;
};

/**
 * Set the thread ID of the active thread.
 * @param  {Object} state Full state reference.
 * @param  {String} id    Thread ID.
 */
export const setActiveThreadId = (state, id) => {
  state.forums.activeThread.id = id;
};

/**
 * Mark the active thread as locked.
 * @param  {Object} state  Full state reference.
 * @param  {Boolean} locked Whether locked or not.
 */
export const setActiveThreadLocked = (state, locked) => {
  state.forums.activeThread.locked = locked;
};

/**
 * Update the active thread's post count.
 * @param  {Object} state     Full state reference.
 * @param  {Number} postCount New post count.
 */
export const setActiveThreadPostCount = (state, postCount) => {
  state.forums.activeThread.postCount = postCount;
};

/**
 * Set active thread title in forums.
 * @param  {Object} state Full state reference.
 * @param  {String} title Title to set.
 */
export const setActiveThreadTitle = (state, title) => {
  state.forums.activeThread.title = title;
};

/**
 * Reset active thread to default state.
 * @param  {Object}  state        Full state reference.
 */
export const resetActiveThread = (state) => {
  state.forums.activeThread = {
    id: null,
    locked: false,
    posts: [],
    poll: null,
    postCount: 0,
  };
};

/**
 * Flags whether the forum thread should be reloaded.
 * @param  {Object}  state        Full state reference.
 * @param  {Boolean} shouldReload Defaults to true
 * @returns {Array}
 */
export const reloadForumThread = (state, shouldReload = true) => {
  state.forums.reload = shouldReload;
};

/**
 * Set the visible threads in the forum Thread List View.
 * @param  {Object}  state           Full state reference.
 * @param  {Array}   options.items   Threads.
 * @param  {Boolean} options.concat  True to add to the list, otherwise overwrite it.
 * @param  {Boolean} options.prepend True to add to the beginning list, otherwise to the end.
 */
export const setForumThreads = (state, {items = [], concat, prepend}) => {
  // Add in the threads.
  state.forums.threads = setItems(state.forums.threads, {items, concat, prepend});
  state.forums.threadsToLoad = [];
};

/**
 * Set the visible threads in the forum Thread List View.
 * @param  {Object}  state            Full state reference.
 * @param  {Array}   options.thread   Thread to update.
 */
export const updateForumThreadTime = (state, {thread}) => {
  const oldThread = state.forums.threads.find(t => t._id === thread._id);
  oldThread.updated = thread.updated;
};

/**
 * Set the visible threads in the forum Thread List View.
 * @param  {Object}  state            Full state reference.
 * @param  {Array}   options.thread   Thread to insert.
 */
export const insertNewForumThread = (state, {thread}) => {
  state.forums.threadsToLoad.push(thread);
};

/**
 * Insert threads that are waiting to be added when the user clicks banner.
 * @param  {Object} state Full state reference.
 */
export const insertWaitingForumThreads = (state) => {
  setForumThreads(state, {items: state.forums.threadsToLoad, concat: true});

  state.forums.threads = state.forums.threads.sort((t1, t2) => {
    if (t1.sticky === true && t2.sticky !== true) {
      return -1;
    } else if (t1.sticky !== true && t2.sticky === true) {
      return 1;
    }

    return new Date(t1.t) > new Date(t2.t) ? -1 : 1;
  });
};

/**
 * Setup the data for a thread poll.
 * @param  {Object} state Full state reference.
 * @param  {Object} poll  Poll data.
 */
export const setThreadPollData = (state, poll) => {
  state.forums.activeThread.poll = poll;
};

/**
 * Set the visible posts in the forum Thread View.
 * @param  {Object}  state           Full state reference.
 * @param  {Array}   options.items   Posts.
 * @param  {Boolean} options.concat  True to add to the list, otherwise overwrite it.
 * @param  {Boolean} options.prepend True to add to the beginning list, otherwise to the end.
 */
export const setForumPosts = (state, {items = [], concat, prepend}) => {
  state.forums.activeThread.posts = setItems(state.forums.activeThread.posts, {items, concat, prepend});
};


export const updateThreadWithPost = (state, {tid, avatar}) => {
  if (!tid) {
    return;
  }

  const thread = state.forums.threads.find(t => t._id === tid) ||
    state.forums.threadsToLoad.find(t => t._id === tid);

  if (thread) {
    thread.postCount += 1;
    if (thread.avatars[0] !== avatar) {
      thread.avatars[1] = avatar;
    }
    thread.read = false;
    thread.t = new Date();
  }
};

/**
 * Set the visible threads in the forum Thread List View.
 * @param  {Object}  state            Full state reference.
 * @param  {Array}   options.thread   Thread to insert.
 */
export const insertThreadReply = (state, {post}) => {
  setForumPosts(state, {items: [post], concat: true});
  state.forums.activeThread.postCount += 1;
};

/**
 * Remove a post from a thread.
 * @param  {Object} state Full state reference.
 * @param  {String} id    Post to delete.
 */
export const removeThreadReply = (state, id) => {
  const index = state.forums.activeThread.posts.findIndex(post => post._id === id);
  if (index > 0) {
    state.forums.activeThread.posts.splice(index, 1);
    state.forums.activeThread.postCount -= 1;
  }
};

/**
 * Update a forum post (if it is loaded).
 * @param  {Object} state            Full state reference.
 * @param  {Object} options
 * @param  {String} options.pid      Post ID.
 * @param  {String} options.message  New post content.
 */
export const editForumPost = (state, {id, message}) => {
  const index = state.forums.activeThread.posts.findIndex(post => post._id === id);
  if (index >= 0) {
    state.forums.activeThread.posts[index].message = message;
  }
};

/**
 * Increment number of thread views on the first post of active thread (if it has a view count).
 * Note that post will have a view count if and only if it is the 1st post of page 0.
 * @param  {Object} state            Full state reference.
 */
export const incForumThreadViews = (state) => {
  const post = state.forums.activeThread.posts[0];
  if (!post || !post.views) {
    return;
  }

  post.views += 1;
};

/**
 * Set the post ID to jump to.
 * @param  {Object} state Full state reference.
 * @param  {String} pid   Post ID.
 */
export const setForumThreadPostId = (state, pid) => {
  state.forums.postId = pid;
};

/**
 * Set the list of shop products.
 * @param  {Object} state    Full state reference.
 * @param  {Array} products List of products.
 */
export const setShopProducts = (state, products) => {
  const game = state.gameData.find(g => g.gid === state.activeGame.gid);
  if (game) {
    game.shop = products;
    state.activeGame.shop = products;
  }
};

/**
 * Set the list of shop products sent from the mobile app.
 * @param  {Object} state              Full state reference.
 * @param  {Array} options.products   Game products.
 * @param  {Array} options.gfProducts GoldFire products.
 */
export const setAppShopProducts = (state, {products, gfProducts}) => {
  // Set the game products.
  const game = state.gameData.find(g => g.gid === state.activeGame.gid);
  if (game) {
    game.appShop = products;
    state.activeGame.appShop = products;
  }

  // Set the GoldFire products.
  state.gameData[0].appShop = gfProducts;
};

/**
 * Open the passed store product in the payment system.
 * @param  {Object} state     Full state reference.
 * @param  {Number} gid       Game ID.
 * @param  {Object} product   Item to be purchased.
 */
export const selectShopProduct = (state, {gid, product, error}) => {
  state.shop.cart = [Object.assign(product, {gid})];
  state.shop.error = error || {};
};

/**
 * Set the gift username for the shop.
 * @param  {Object} state Full state reference.
 * @param  {String} user  Username.
 */
export const setShopGift = (state, user) => {
  state.shop.gift = user;
};

/**
 * Set the cards that this player has saved.
 * @param  {Object} state Full state reference.
 * @param  {Array} cards Cards data.
 */
export const setShopCards = (state, cards) => {
  state.shop.manage.cards = cards || [];
};

/**
 * Set the active shop subscriptions for this player..
 * @param  {Object} state Full state reference.
 * @param  {Array} subs Subscriptions data.
 */
export const setShopSubscriptions = (state, subs) => {
  state.shop.manage.subs = (subs || []).map(sub => Object.assign(sub, {deleting: false}));
};

/**
 * Set whether or no the PayPal transaction has completed.
 * @param  {Object} state  Full state reference.
 * @param  {Boolean} status Whether complete or not.
 */
export const setPaypalStatus = (state, status) => {
  state.shop.paypal = status;
};

/**
 * Set the Xsolla access token.
 * @param  {Object} state Full state reference.
 * @param  {String} token Access token.
 */
export const setXsollaToken = (state, token) => {
  state.shop.xsolla = token;
};

/**
 * Toggle the delete confirmation for a credit card.
 * @param  {Object} state Full state reference.
 * @param  {Number} index Index to delete.
 */
export const toggleDeleteCC = (state, index) => {
  state.shop.manage.cards[index].deleting = !state.shop.manage.cards[index].deleting;
};


export const toggleDeleteSub = (state, index) => {
  state.shop.manage.subs[index].deleting = !state.shop.manage.subs[index].deleting;
};

/**
 * Delete a credit card on the client.
 * @param  {Object} state Full state reference.
 * @param  {Number} index Index of the card.
 */
export const deleteCC = (state, index) => {
  state.shop.manage.cards.splice(index, 1);
};

/**
 * Delete a subscription on the client.
 * @param  {Object} state Full state reference.
 * @param  {Number} index Index of the subscription.
 */
export const deleteSubscription = (state, index) => {
  state.shop.manage.subs[index].cancel = true;
  state.shop.manage.subs[index].deleting = false;
};

/**
 * Set the launcher games initial data.
 * @param  {Object} state              Full state reference.
 * @param  {Array} options.games      Games to add to the list and their data.
 */
export const setLauncherGames = (state, {games}) => {
  // Add games that we aren't connected to.
  games = state.gameData.map((data) => {
    const match = games.find(game => game.game === data.gid);

    if (!match && data.hidden) {
      return null;
    }

    return {
      game: data.gid,
      on: match ? match.on : 0,
      active: !!match,
      notifications: match ? match.notifications : 0,
      rank: match ? match.rank : 0,
    };
  }).filter(d => d);

  // Sort the games by how frequently they are played.
  games.sort((a, b) => {
    if (a.on > b.on) {
      return -1;
    }
    if (a.on < b.on) {
      return 1;
    }
    return 0;
  });

  games = games.map((game) => {
    delete game.on;
    return game;
  });

  // Update the games list.
  state.launcher.games = games;
};

/**
 * Update the rank on a launcher game.
 * @param  {Object} state         Full state reference.
 * @param  {Array} options.games Games to update.
 */
export const setLauncherGamesRank = (state, {games}) => {
  games = state.launcher.games.map((data) => {
    const match = games.find(game => game.game === data.game);

    return {
      game: data.game,
      active: data.active,
      notifications: data.notifications || 0,
      rank: match ? match.rank : 0,
    };
  });

  // Update the games list.
  state.launcher.games = games;
};

/**
 * Set the game ID of the notifications view.
 * @param  {Object} state Full state reference.
 * @param  {Number} id    Game ID.
 */
export const setNotificationsId = (state, id) => {
  state.launcher.notificationsId = id;
};

/**
 * Set the notifications data, or add to the list.
 * @param  {Object} state            Full state reference.
 * @param  {Array}  options.items    Notifications to set.
 * @param  {Boolean} options.concat  Whether to overwrite or concat.
 * @param  {String} options.prepend  Option to prepend the list with the new items.
 */
export const setNotifications = (state, {items = [], concat, prepend}) => {
  state.launcher.notifications = setItems(state.launcher.notifications, {items, concat, prepend});
};

/**
 * Add a new notification to the popup queue.
 * @param  {Object} state        Full state reference.
 * @param  {Object} notification Notification data.
 */
export const addNotificationToQueue = (state, notification) => {
  // Don't show the notification popup if they are blocking it.
  if (!state.user.notices && notification.type !== 'medal') {
    return;
  }

  // Create the data to insert.
  const popup = {
    text: notification.text,
    icon: state.gameData.find(game => game.gid === notification.gid).game,
    type: notification.type,
    user: notification.user,
    t: notification.t,
    _id: notification._id,
    gid: notification.gid,
    avatar: notification.avatar,
    chat: notification.chat,
    act: notification.act,
    ref: notification.ref,
    role: notification.role,
  };

  // Add to the queue.
  state.notices.push(popup);

  // Play the notification sound.
  if (popup.type !== 'medal') {
    playSound(state, 'notification');
  }
};

/**
 * Remove notification from popup queue.
 * @param  {Object} state Full state reference.
 * @param  {String} id    Notification ID.
 */
export const remNotificationFromQueue = (state, id) => {
  const notice = state.notices.findIndex(data => data._id === id);
  if (notice >= 0) {
    state.notices.splice(notice, 1);
  }
};

/**
 * Increment game notifications counter.
 * @param  {Object} state                Full state reference.
 * @param  {Object} options.notification Notification data.
 * @param  {Number} options.inc          Amount to increment by.
 */
export const incGameNotifications = (state, {notification, inc = 1}) => {
  const game = state.launcher.games.find(data => data.game === notification.gid);
  game.notifications += inc;
};

/**
 * Reset game notifications to 0.
 * @param  {Object} state Full state reference.
 * @param  {Number} gid   Game ID.
 */
export const resetGameNotifications = (state, gid) => {
  const game = state.launcher.games.find(data => data.game === gid);
  game.notifications = 0;
};

/**
 * Increment toolbar notifications counter.
 * @param  {Object} state Full state reference.
 * @param  {Number} inc   Amount to increment by.
 */
export const incToolbarNotifications = (state, inc = 1) => {
  state.launcher.counter += inc;
};

/**
 * Set toolbar notifications counter.
 * @param  {Object} state Full state reference.
 * @param  {Number} val   Amount to set.
 */
export const setToolbarNotifications = (state, val = 0) => {
  state.launcher.counter = val;
};

/**
 * Set toolbar messages counter.
 * @param  {Object} state Full state reference.
 * @param  {Number} val   Amount to set.
 */
export const setToolbarMessages = (state, val = 0) => {
  state.chat.counter = val;
};

/**
 * Increment social feed panel counter.
 * @param  {Object} state Full state reference.
 * @param  {Number} inc   Amount to increment by.
 */
export const incSocialFeedCounter = (state, inc = 1) => {
  state.socialFeed.counter += inc;

  // Prevent the social feed from going negative on deletes.
  if (state.socialFeed.counter < 0) {
    state.socialFeed.counter = 0;
  }
};

/**
 * Increment profile panel social feed counter.
 * @param  {Object} state Full state reference.
 * @param  {Number} inc   Amount to increment by.
 */
export const incProfileSocialFeedCounter = (state, inc = 1) => {
  state.launcher.profile.updateCount += inc;
};

/**
 * Set toolbar forum counter.
 * @param  {Object} state Full state reference.
 * @param  {Number} inc   Amount to set.
 */
export const setForumCounter = (state, val = 0) => {
  state.forums.counter = val;
};

/**
 * Update the social feed data (whether adding or replacing).
 * @param  {Object} state           Full state reference.
 * @param  {Array} options.items    List of social feed updates.
 * @param  {Boolean} options.concat True to add to the list.
 * @param  {String} options.prepend  Option to prepend the list with the new items.
 */
export const setSocialFeedUpdates = (state, {items = [], concat, prepend}) => {
  state.socialFeed.updates = setItems(state.socialFeed.updates, {items, concat, prepend});
};

/**
 * Update the shop history data (whether adding or replacing).
 * @param  {Object} state          Full state reference.
 * @param  {Array} options.items  List of shop history items.
 * @param  {Boolean} options.concat True to add to the list.
 * @param  {String} options.prepend  Option to prepend the list with the new items.
 */
export const setShopHistory = (state, {items = [], concat, prepend}) => {
  state.shop.history = setItems(state.shop.history, {items, concat, prepend});
};

/**
 * Add a comment to the update.
 * @param  {Object} state            Full state reference.
 * @param  {String} options.updateId _id of the update commented.
 * @param  {Object} options.comment  Data about the comment.
 */
export const concatComment = (state, {updateId, comment}) => {
  const update = state.socialFeed.updates.find(u => u._id === updateId);
  if (!update) {
    return;
  }
  update.comments.push(comment);
};

/**
 * Set the categories
 * @param  {Object} state            Full state reference.
 * @param  {String} options.categories  categories to assign to state.
 */
export const setLeaderboardCategories = (state, {categories}) => {
  state.leaderboards.categories = categories;
};


/**
 * Set the players for the list of a specific leaderboard
 * @param  {Object} state            Full state reference.
 * @param  {String} options.items    Players to set into list.
 * @param  {String} options.concat   Option to concat the new players onto existing.
 * @param  {String} options.prepend  Option to prepend the list with the new items.
 */
export const setLeaderboardPlayers = (state, {items = [], concat, prepend}) => {
  state.leaderboards.players =
    setItems(state.leaderboards.players, {items, concat, prepend});
};

/**
 * Toggle showing all users or just friends on leaderboard
 * @param  {Object} state            Full state reference.
 */
export const toggleLeaderboardUsers = (state) => {
  setLeaderboardPlayers(state, {});
  state.leaderboards.allUsers = !state.leaderboards.allUsers;
  state.panel.buttons = [
    {label: state.leaderboards.allUsers ? 'All Users' : 'Friends', view: {mutation: 'toggleLeaderboardUsers'}},
  ];

  if (state.leaderboards.activeCategory.monthly && state.leaderboards.activeCategory.all) {
    state.panel.buttons.push(
      {label: state.leaderboards.allTime ? 'All-Time' : 'Month', view: {mutation: 'toggleLeaderboardTime'}}
    );
  }
};

/**
 * Toggle showing leaderboard for all time or month
 * @param  {Object} state            Full state reference.
 */
export const toggleLeaderboardTime  = (state) => {
  setLeaderboardPlayers(state, {});
  state.leaderboards.allTime = !state.leaderboards.allTime;
  state.panel.buttons = [
    {label: state.leaderboards.allUsers ? 'All Users' : 'Friends', view: {mutation: 'toggleLeaderboardUsers'}},
  ];

  if (state.leaderboards.activeCategory.monthly && state.leaderboards.activeCategory.all) {
    state.panel.buttons.push({
      label: state.leaderboards.allTime ? 'All-Time' : 'Month',
      view: {mutation: 'toggleLeaderboardTime'},
    });
  }
};

/**
 * Set the number of online players per game.
 * @param  {Object} state         Full state reference.
 * @param  {Array}  options.games Games to update.
 */
export const setGamesOnline = (state, {games = []}) => {
  games.forEach((game) => {
    const index = state.gameData.findIndex(data => data.gid === game.gid);
    if (index) {
      state.gameData[index].online = game.online;
    }
  });
};

/**
 * Increment the online players counters for games.
 * @param  {Object} state        Full state reference.
 * @param  {Number} options.game Game ID.
 * @param  {Number} options.val  Amount to increment by.
 */
export const incGameOnlineCounters = (state, {game, val = 0}) => {
  const index = state.gameData.findIndex(data => data.gid === game);
  if (index) {
    state.gameData[index].online += val;
  }
};

/**
 * Set the settings data.
 * @param  {Object} state Full state reference.
 * @param  {Object} data  Data to set.
 */
export const setSettingsData = (state, data) => {
  if (typeof data.verify !== 'undefined') {
    state.settings.verify = data.verify;
  }
  if (typeof data.flags !== 'undefined') {
    state.settings.flags = data.flags || {};
  }
  state.settings.email = data.email || '';
  state.settings.user = data.user || '';
  state.settings.pass = data.pass || '';
  state.settings.multiFactor = data.multiFactor || false;
  state.settings.loc = data.loc || '';
  state.settings.gender = (data.gender || data.sex) || '';
  state.settings.emails = typeof data.emails === 'boolean' ? data.emails : true;
  state.settings.notices = typeof data.notices === 'boolean' ? data.notices : true;
  state.settings.openchat = typeof data.openchat === 'boolean' ? data.openchat : false;
  state.settings.mute = typeof data.mute === 'boolean' ? data.mute : false;
  state.settings.left = typeof data.left === 'boolean' ? data.left : false;
};

/**
 * Set the settings error states.
 * @param  {Object} state Full state reference.
 * @param  {Object} error Errors to set.
 */
export const setSettingsErrors = (state, error) => {
  Object.keys(state.settings.error).forEach((key) => {
    state.settings.error[key] = typeof error[key] === 'boolean' ? error[key] : false;
  });
};

/**
 * Set the user-defined direction of the toolbar.
 * @param  {Object} state Full state reference.
 * @param  {Boolean} left  Whether it should be left or not.
 */
export const setToolbarDirection = (state, left) => {
  state.toolbar.left = left || false;
};

/**
 * Set the list of online players for the info view.
 * @param  {Object} state   Full state reference.
 * @param  {Array} players List of players.
 */
export const setOnlinePlayers = (state, players) => {
  state.info.online = players;
};

/**
 * Add a player to the online players list and re-sort.
 * @param  {Object} state  Full state reference.
 * @param  {Object} player Data about the player to add.
 */
export const addOnlinePlayers = (state, player) => {
  const index = state.info.online.findIndex(plr => plr.uid === player.uid);
  if (index < 0) {
    // Add the new player, re-sort the list and then update the list.
    const players = [...state.info.online];
    players.push(player);
    players.sort((a, b) => {
      if (a.role && !b.role) {
        return -1;
      } else if (!a.role && b.role) {
        return 1;
      }

      return new Date(a.t) > new Date(b.t) ? -1 : 1;
    });
    setOnlinePlayers(state, players);
  }
};

/**
 * Remove a player from the online list.
 * @param  {Object} state  Full state reference.
 * @param  {Object} player Player to remove.
 */
export const remOnlinePlayers = (state, player) => {
  const index = state.info.online.findIndex(plr => plr.uid === player.uid);
  if (index >= 0) {
    state.info.online.splice(index, 1);
  }
};

/**
 * Set the active game data.
 * @param  {Object} state      Full state reference.
 * @param  {Object} activeGame Active game data.
 */
export const setActiveGame = (state, activeGame) => {
  state.activeGame = state.gameData.find(game => game.game === activeGame) || {};
  state.leaderboards.gid = state.activeGame.gid;
  state.leaderboards.uid = state.user.uid;
};

/**
 * Set leaderboard game ID and data.
 * @param  {Object} state        Full state reference.
 * @param  {Number} options.gid  Game ID.
 * @param  {String} options.uid  User ID.
 * @param  {String} options.user Username.
 */
export const setLeaderboardGame = (state, {gid, uid, user}) => {
  state.leaderboards.gid = gid;
  state.leaderboards.uid = uid;
  state.leaderboards.user = user;
};

/**
 * Set the current app type.
 * @param  {Object} state Full state reference.
 * @param  {String} app   App type.
 */
export const setAppType = (state, app) => {
  state.app.type = app;
};

/**
 * Set where or not to show the login display on the game page.
 * @param  {Object} state Full state reference.
 * @param  {Boolean} show  Show or not.
 */
export const showLogin = (state, show) => {
  state.auth.showLogin = show;
};

/**
 * Set the Steam app meta data for the user.
 * @param  {Object} state         Full state reference.
 * @param  {String} options.id    Steam ID.
 * @param  {String} options.lang  Steam language.
 * @param  {String} options.appId Steam app ID.
 */
export const setSteamData = (state, {id, lang, appId}) => {
  state.app.steam.id = id;
  state.app.steam.lang = lang;
  state.app.steam.appId = appId;
};

/**
 * Update the status of hiding the iframe (used to re-create the DOM element to fix memory leak).
 * @param  {Object} state Full state reference.
 * @param  {Boolean} hide  Whether or not to hide the iframe.
 */
export const setHideIframe = (state, hide) => {
  state.launcher.hideIframe = hide;
};

/**
 * Update the global now time to Date.now().
 * @param  {Object} state Full state reference.
 */
export const updateTime = (state) => {
  state.now = Date.now();
};

/**
 * Set the updates data (show and data) for the active game.
 * @param  {Object} state        Full state reference.
 * @param  {Boolean} options.show Show/hide the updates modal.
 * @param  {Boolean} options.all Whether or not all updates are being shown.
 * @param  {Array} options.data List of updates data to show.
 * @param  {Boolean} options.reviews Whether or not to show review prompt.
 */
export const setUpdates = (state, {show, all, data, reviews}) => {
  if (typeof show === 'boolean') {
    state.updates.show = show;
  }
  if (typeof all === 'boolean') {
    state.updates.all = all;
  }
  if (typeof data === 'object') {
    state.updates.data = data;
  }
  if (typeof reviews === 'boolean') {
    state.updates.reviews = reviews;
  }
};
